import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import talentLogo2 from "../assets/talentlogo2.png";
import usePageMeta from "../hooks/usePageMeta";
import { BASE_URL } from "../config";
import { loadManageContext, saveManageContext } from "./manageContext";
import { formatAppTime } from "../utils/appDateTime";

function hashHue(str) {
  let h = 0;
  for (let i = 0; i < String(str).length; i++) {
    h = String(str).charCodeAt(i) + ((h << 5) - h);
  }
  return Math.abs(h) % 360;
}

function avatarBgFallback(name) {
  return `hsl(${hashHue(name || "user")} 42% 42%)`;
}

function normalizeHexColor(raw) {
  const s = String(raw || "").trim();
  if (!s) return null;
  if (/^#[0-9A-Fa-f]{3}$/.test(s) || /^#[0-9A-Fa-f]{6}$/.test(s)) return s;
  if (/^[0-9A-Fa-f]{6}$/.test(s)) return `#${s}`;
  return null;
}

function ManageContactAvatar({ name, profileImageUrl, avatarColor, avatarAbbrev }) {
  const [imgErr, setImgErr] = useState(false);

  const initials = useMemo(() => {
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    const a = (parts[0]?.[0] || "?").toUpperCase();
    const b = (parts[1]?.[0] || "").toUpperCase();
    if (b) return `${a}${b}`;
    return `${a}${(parts[0]?.[1] || "").toUpperCase()}`.slice(0, 2);
  }, [name]);

  const abbrev =
    String(avatarAbbrev || "")
      .trim()
      .slice(0, 3)
      .toUpperCase() || initials;

  const bg = normalizeHexColor(avatarColor) || avatarBgFallback(name);

  if (profileImageUrl && !imgErr) {
    return (
      <img
        src={profileImageUrl}
        alt=""
        className="h-8 w-8 shrink-0 rounded-full object-cover ring-1 ring-black/5"
        onError={() => setImgErr(true)}
      />
    );
  }

  return (
    <div
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white shadow-sm"
      style={{ backgroundColor: bg }}
    >
      {abbrev.slice(0, 2)}
    </div>
  );
}

export default function Manage() {
  usePageMeta("Manage Area");
  const navigate = useNavigate();
  const location = useLocation();
  const manageContext = useMemo(() => loadManageContext(location), [location]);
  const { clientId, clientName, email } = manageContext;
  const selectedLocationIdsKey = Array.isArray(manageContext.selectedLocationIds)
    ? manageContext.selectedLocationIds.join(",")
    : "";
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState("");
  const [resolvedClientId, setResolvedClientId] = useState(clientId || null);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    saveManageContext(manageContext);
  }, [manageContext]);

  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    setResolvedClientId(clientId || null);
  }, [clientId]);

  useEffect(() => {
    let cancelled = false;

    const resolveClientId = async () => {
      if (clientId) return;

      try {
        const fallbackQuery = (clientName || email || "").trim();
        if (!fallbackQuery) return;

        const params = new URLSearchParams({
          search: fallbackQuery,
          limit: "50",
          page: "1",
        });
        const res = await fetch(`${BASE_URL}/api/clients/contacts?${params.toString()}`);
        if (!res.ok) return;

        const payload = await res.json();
        const rows = Array.isArray(payload?.data) ? payload.data : [];

        let match = null;
        if (email) {
          match = rows.find(
            (r) => String(r?.email || "").toLowerCase() === String(email).toLowerCase(),
          );
        }
        if (!match && rows.length === 1) {
          match = rows[0];
        }

        const nextClientId = match?.clientId ? Number(match.clientId) : null;
        if (!cancelled && nextClientId) {
          setResolvedClientId(nextClientId);
        }
      } catch {
        // Keep fallback UI if resolution fails.
      }
    };

    resolveClientId();
    return () => {
      cancelled = true;
    };
  }, [clientId, clientName, email]);

  useEffect(() => {
    let cancelled = false;

    const loadClientContacts = async () => {
      if (!resolvedClientId) {
        setContacts([]);
        return;
      }

      setContactsLoading(true);
      setContactsError("");

      try {
        const scopeIds = Array.isArray(manageContext.selectedLocationIds)
          ? manageContext.selectedLocationIds.map((id) => String(id).trim()).filter(Boolean)
          : [];
        let url = `${BASE_URL}/api/clients/${encodeURIComponent(resolvedClientId)}/manage-contacts`;
        if (scopeIds.length > 0) {
          url += `?locationIds=${encodeURIComponent(scopeIds.join(","))}`;
        }
        const res = await fetch(url);
        if (!res.ok) {
          throw new Error(`Failed to load contacts (${res.status})`);
        }

        const payload = await res.json();
        const rows = Array.isArray(payload?.data) ? payload.data : [];
        const normalized = rows.map((row) => {
          const first = String(row?.first_name || "").trim();
          const last = String(row?.last_name || "").trim();
          const fullName = `${first} ${last}`.trim() || row?.name || "User";
          return {
            id:
              row?.accountId ||
              row?.account_id ||
              row?.contact_id ||
              row?.id ||
              null,
            name: fullName,
            email: row?.email || "",
            profileImageUrl: row?.profile_image_url || row?.profileImageUrl || "",
            avatarColor: row?.avatar_color || "",
            avatarAbbrev: row?.avatar_abbreviation || "",
            roles: Array.isArray(row?.roles) ? row.roles : [],
            permissions: Array.isArray(row?.permissions) ? row.permissions : [],
            capabilities: row?.capabilities || null,
            role:
              (Array.isArray(row?.roles) && row.roles.length
                ? row.roles.join(", ")
                : row?.role) || "Contact",
          };
        });

        if (!cancelled) {
          setContacts(normalized);
        }
      } catch (err) {
        if (!cancelled) {
          setContacts([]);
          setContactsError(err?.message || "Failed to load contacts");
        }
      } finally {
        if (!cancelled) {
          setContactsLoading(false);
        }
      }
    };

    loadClientContacts();
    return () => {
      cancelled = true;
    };
  }, [resolvedClientId, selectedLocationIdsKey]);

  const handleClientClick = (selectedContact = null) => {
    const selectedEmail = selectedContact?.email || email;
    const selectedAccountId = selectedContact?.id || manageContext.accountId || null;
    const selectedContactRole = selectedContact?.role || manageContext.contactRole || "";
    const selectedClientId = resolvedClientId || manageContext.clientId || null;
    const selectedClientName = clientName || manageContext.clientName || "Client";
    const nextContext = {
      ...manageContext,
      email: selectedEmail,
      clientName: selectedClientName,
      accountId: selectedAccountId,
      clientId: selectedClientId,
      contactRole: selectedContactRole,
      capabilities: selectedContact?.capabilities || null,
      permissions: selectedContact?.permissions || [],
      roles: selectedContact?.roles || [],
    };

    const params = new URLSearchParams({
      email: selectedEmail,
      name: selectedClientName,
    });
    if (selectedAccountId) params.set("accountId", String(selectedAccountId));
    if (selectedClientId) params.set("clientId", String(selectedClientId));
    if (selectedContactRole) params.set("contactRole", selectedContactRole);
    if (nextContext.capabilities) {
      params.set("capabilities", JSON.stringify(nextContext.capabilities));
    }
    if (manageContext.shiftId) params.set("shiftId", String(manageContext.shiftId));
    if (manageContext.timesheetId) params.set("timesheetId", String(manageContext.timesheetId));
    if (nextContext.locationName) params.set("location", String(nextContext.locationName));
    if (nextContext.shiftDate) params.set("shiftDate", String(nextContext.shiftDate));
    if (nextContext.startDate) params.set("from", String(nextContext.startDate));
    if (nextContext.endDate) params.set("to", String(nextContext.endDate));
    if (Array.isArray(manageContext.selectedLocationIds) && manageContext.selectedLocationIds.length) {
      params.set("selectedLocationIds", manageContext.selectedLocationIds.join(","));
    }

    navigate(`/manage/password?${params.toString()}`, {
      state: { manageContext: nextContext },
    });
  };

  const handleCancel = () => {
    const urlParams = new URLSearchParams(location.search);
    if (!urlParams.has("clientId") && !urlParams.has("name") && !urlParams.has("client")) {
      navigate("/device");
      return;
    }
    const checkinParams = new URLSearchParams();
    if (clientId) checkinParams.set("clientId", String(clientId));
    if (manageContext.shiftDate) checkinParams.set("shiftDate", manageContext.shiftDate);
    if (manageContext.startDate) checkinParams.set("from", manageContext.startDate);
    if (manageContext.endDate) checkinParams.set("to", manageContext.endDate);
    if (clientName) checkinParams.set("client", clientName);
    if (manageContext.locationName) checkinParams.set("location", manageContext.locationName);
    
    const forceLoginVal = urlParams.get("forceLogin") || "1";
    checkinParams.set("forceLogin", forceLoginVal);

    navigate(`/checkin?${checkinParams.toString()}`);
  };

  const formatTime = (d) => formatAppTime(d);

  const contactsToShow = contacts;

  return (
    <div className="flex h-screen max-h-[100dvh] flex-col overflow-hidden bg-[#f5f5f5]">
      <header className="flex shrink-0 items-start justify-between px-4 pt-4">
        <div className="cursor-pointer" onClick={handleCancel}>
          <img src={talentLogo2} alt="Talent Logo" className="h-12 w-auto" />
        </div>
        <div className="text-right">
          <div className="text-xl font-bold text-gray-900">{formatTime(currentTime)}</div>
          <div className="text-sm text-gray-500">{clientName || "Client"}</div>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4">
        <div className="mx-auto w-full max-w-xl pb-6">
          <h1 className="mb-1 text-center text-2xl font-normal text-gray-700">Manage Area</h1>
          <p className="mb-4 text-sm text-gray-500">Sign-in</p>

          {contactsLoading && (
            <div className="mb-3 text-center text-xs text-gray-500">Loading contacts...</div>
          )}
          {contactsError && <div className="mb-3 text-center text-xs text-red-600">{contactsError}</div>}
          {!contactsLoading && contacts.length === 0 && !contactsError && (
            <div className="mb-3 text-center text-sm text-gray-500">
              No client contacts found for this client.
            </div>
          )}

          <div className="overflow-hidden rounded border border-gray-200 bg-white">
            <div className="divide-y divide-gray-200">
              {contactsToShow.map((contact) => (
                <button
                  key={contact.id}
                  type="button"
                  onClick={() => handleClientClick(contact)}
                  className="flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-gray-50"
                >
                  <ManageContactAvatar
                    name={contact.name}
                    profileImageUrl={contact.profileImageUrl}
                    avatarColor={contact.avatarColor}
                    avatarAbbrev={contact.avatarAbbrev}
                  />
                  <span className="min-w-0 flex-1 text-base font-normal text-orange-600">
                    {contact.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <footer className="shrink-0 px-4 pb-6 pt-2">
        <button
          type="button"
          onClick={handleCancel}
          className="w-full py-3 text-center text-sm font-medium uppercase tracking-wide text-orange-600 hover:text-orange-700"
        >
          CANCEL
        </button>
      </footer>
    </div>
  );
}
